import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, Form, Input, Button, Typography, Alert, Space, Row, Col, Spin } from 'antd'
import { MailOutlined, PhoneOutlined, FormOutlined, UserOutlined, HomeOutlined } from '@ant-design/icons'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { env } from '../config/environment'
import { 
  getCustomerProfile, 
  clearExpiredProfiles,
  clearCustomerProfile
} from '../utils/customerStorage'

const { Title, Text } = Typography

// Module-level logging to confirm file is loaded
console.log('📦 FormEntry.tsx module loaded at', new Date().toISOString())

export const FormEntry: React.FC = () => {
  console.log('🎬 FormEntry component function called')
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, portalConfig } = useCustomerAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [authForm] = Form.useForm()
  const [createForm] = Form.useForm()
  const [autoAuthLoading, setAutoAuthLoading] = useState(true)
  const [checkingSubmission, setCheckingSubmission] = useState(false)
  const redirectAttemptedRef = useRef<string | null>(null) // Store token to track if we've checked this specific token

  console.log('📄 FormEntry component rendered', { 
    token, 
    autoAuthLoading, 
    checkingSubmission,
    timestamp: new Date().toISOString(),
    pathname: window.location.pathname
  })

  // Check if form was already submitted and redirect accordingly
  const checkFormSubmissionAndRedirect = useCallback(async (authToken: string) => {
    if (!token) {
      console.warn('⚠️ No token available for redirect check')
      return false
    }

    setCheckingSubmission(true)
    try {
      console.log('🔍 Checking if form was already submitted...')
      const response = await fetch(
        `${env.apiBaseUrl}/customer-forms/public/${token}/check-submission`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      )

      if (response.ok) {
        const result = await response.json()
        if (result.alreadySubmitted) {
          console.log('✅ Form already submitted, redirecting to portal...')
          const defaultLandingPage = portalConfig?.defaultLandingPage || '/dashboard'
          window.location.href = defaultLandingPage
          return true
        } else {
          console.log('ℹ️ Form not yet submitted, redirecting to form completion...')
          window.location.href = `/forms/${token}/complete`
          return true
        }
      } else if (response.status === 401 || response.status === 404) {
        // Auth failed or customer not found - clear token and show access form
        console.log('⚠️ Authentication issue, clearing token...')
        localStorage.removeItem('customerToken')
        if (token) {
          clearCustomerProfile(token)
        }
        return false
      }
      
      // If check failed, redirect to form completion page
      console.log('ℹ️ Redirecting to form completion page...')
      window.location.href = `/forms/${token}/complete`
      return true
    } catch (error) {
      console.error('Error checking form submission:', error)
      // On error, redirect to form completion page
      console.log('ℹ️ Error occurred, redirecting to form completion page...')
      window.location.href = `/forms/${token}/complete`
      return true
    } finally {
      setCheckingSubmission(false)
    }
  }, [token, portalConfig])

  // Clear expired profiles on mount
  useEffect(() => {
    clearExpiredProfiles()
  }, [])

  // Prepopulate forms with URL query parameters
  useEffect(() => {
    if (!autoAuthLoading) {
      const emailParam = searchParams.get('email')
      const phoneParam = searchParams.get('phone')
      
      if (emailParam || phoneParam) {
        console.log('📝 Prepopulating forms with URL query parameters', { emailParam, phoneParam })
        
        // Prepopulate auth form
        const authValues: any = {}
        if (emailParam) {
          authValues.email = emailParam
        }
        if (phoneParam) {
          authValues.phone = phoneParam
        }
        if (Object.keys(authValues).length > 0) {
          authForm.setFieldsValue(authValues)
        }
        
        // Prepopulate create form
        const createValues: any = {}
        if (emailParam) {
          createValues.email = emailParam
        }
        if (phoneParam) {
          createValues.phone = phoneParam
        }
        if (Object.keys(createValues).length > 0) {
          createForm.setFieldsValue(createValues)
        }
      }
    }
  }, [autoAuthLoading, searchParams, authForm, createForm])

  // Reset redirect attempt when component unmounts or token changes
  useEffect(() => {
    return () => {
      // Reset on unmount so it runs again when component remounts
      redirectAttemptedRef.current = null
      console.log('🧹 FormEntry unmounting, resetting redirect attempt')
    }
  }, [])

  // Reset redirect attempt when token changes
  useEffect(() => {
    if (token) {
      console.log('🔄 Token detected, preparing redirect check', { token })
      redirectAttemptedRef.current = null
      setAutoAuthLoading(true)
    }
  }, [token])

  // Immediate check on mount - check authentication and redirect accordingly
  useEffect(() => {
    if (!token) {
      console.log('⚠️ No token found in URL params')
      setAutoAuthLoading(false)
      return
    }

    // Prevent concurrent runs for the same token
    const currentToken = token
    if (redirectAttemptedRef.current === currentToken) {
      console.log('⏭️ Already checking redirect for this token, skipping duplicate...')
      return
    }

    console.log('🚀 FormEntry: Starting immediate redirect check...', { token })
    
    const handleImmediateRedirect = async () => {
      redirectAttemptedRef.current = currentToken

      // First, check if there's a token in localStorage (most direct check)
      const generalToken = localStorage.getItem('customerToken')
      
      if (generalToken) {
        console.log('🔐 Found customerToken in localStorage, checking submission status...')
        // Immediately check submission status without waiting for React state
        try {
          const redirected = await checkFormSubmissionAndRedirect(generalToken)
          if (redirected) {
            // Redirect is happening, don't show the form
            return
          }
        } catch (error) {
          console.error('Error in checkFormSubmissionAndRedirect:', error)
        }
        // If redirect failed (auth issue), continue to try stored profile
      } else {
        console.log('ℹ️ No customerToken in localStorage')
      }

      // Check for form-specific stored profile
      const storedProfile = getCustomerProfile(token)
      
      if (storedProfile && storedProfile.token) {
        console.log('🔐 Found stored customer profile for form, attempting auto-authentication...')
        try {
          await login(storedProfile.token)
          console.log('✅ Auto-authentication successful')
          // Now check submission status
          try {
            const redirected = await checkFormSubmissionAndRedirect(storedProfile.token)
            if (redirected) {
              return
            }
          } catch (error) {
            console.error('Error checking submission after login:', error)
          }
        } catch (error) {
          console.error('❌ Auto-authentication failed:', error)
          // Clear invalid stored profile
          clearCustomerProfile(token)
        }
      } else {
        console.log('ℹ️ No stored customer profile found for form')
      }

      // If we get here, no valid authentication found - show access form
      console.log('📝 No valid authentication found, showing access form')
      setAutoAuthLoading(false)
      // Reset ref so it can run again if user navigates back
      redirectAttemptedRef.current = null
    }

    // Run immediately - use setTimeout to ensure it runs after component is fully mounted
    const timeoutId = setTimeout(() => {
      handleImmediateRedirect()
    }, 0)

    // Cleanup on unmount or token change
    return () => {
      clearTimeout(timeoutId)
    }
  }, [token, login, checkFormSubmissionAndRedirect])

  const handleAuthenticate = async (values: any) => {
    try {
      setLoading(true)
      setError(null)

      // Call the form authentication endpoint
      const response = await fetch(`${env.apiBaseUrl}/customer-forms/public/${token}/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email || undefined,
          phone: values.phone || undefined,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Check if customer needs to be created
        if (data.requiresCreation) {
          setShowCreateForm(true)
          createForm.setFieldsValue({
            email: data.providedEmail,
            phone: data.providedPhone,
          })
          return
        }
        throw new Error(data.message || 'Authentication failed')
      }

      // Login with the portal token
      await login(data.data.portalToken)
      
      // Redirect to form completion page
      navigate(`/forms/${token}/complete`, { replace: true })

    } catch (err: any) {
      console.error('Form authentication error:', err)
      setError(err.message || 'Authentication failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCustomer = async (values: any) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`${env.apiBaseUrl}/customer-forms/public/${token}/create-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName || '',
          email: values.email || undefined,
          phone: values.phone || undefined,
          address: values.address || undefined,
          city: values.city || undefined,
          state: values.state || undefined,
          zipCode: values.zipCode || undefined,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create account')
      }

      // Login with the portal token
      await login(data.data.portalToken)
      
      // Redirect to form completion page
      navigate(`/forms/${token}/complete`, { replace: true })

    } catch (err: any) {
      console.error('Customer creation error:', err)
      setError(err.message || 'Failed to create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Show loading spinner while attempting auto-authentication or checking submission
  if (autoAuthLoading || checkingSubmission) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '16px'
      }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '16px'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 480,
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <FormOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
              {showCreateForm ? 'Create Your Account' : 'Access Form'}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {showCreateForm 
                ? 'Please provide your information to continue'
                : 'Enter your email or phone number to continue'
              }
            </Text>
          </div>

          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          {!showCreateForm ? (
            <Form
              form={authForm}
              layout="vertical"
              onFinish={handleAuthenticate}
            >
              <Form.Item
                name="email"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value && !authForm.getFieldValue('phone')) {
                        return Promise.reject(new Error('Please enter either email or phone'))
                      }
                      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                        return Promise.reject(new Error('Please enter a valid email'))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email address"
                  size="large"
                />
              </Form.Item>

              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <Text type="secondary">OR</Text>
              </div>

              <Form.Item
                name="phone"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value && !authForm.getFieldValue('email')) {
                        return Promise.reject(new Error('Please enter either email or phone'))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Phone number"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{ height: '48px' }}
                >
                  Continue
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Form
              form={createForm}
              layout="vertical"
              onFinish={handleCreateCustomer}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: 'First name is required' }]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="First name"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: 'Last name is required' }]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Last name"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value && !createForm.getFieldValue('phone')) {
                        return Promise.reject(new Error('Please enter either email or phone'))
                      }
                      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                        return Promise.reject(new Error('Please enter a valid email'))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email address"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Phone"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value && !createForm.getFieldValue('email')) {
                        return Promise.reject(new Error('Please enter either email or phone'))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Phone number"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="address"
                label="Address"
              >
                <Input
                  prefix={<HomeOutlined />}
                  placeholder="Street address"
                  size="large"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="city"
                    label="City"
                  >
                    <Input
                      placeholder="City"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item
                    name="state"
                    label="State"
                  >
                    <Input
                      placeholder="State"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item
                    name="zipCode"
                    label="ZIP"
                  >
                    <Input
                      placeholder="ZIP"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{ height: '48px' }}
                >
                  Create Account & Continue
                </Button>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                <Button
                  type="link"
                  onClick={() => {
                    setShowCreateForm(false)
                    setError(null)
                    authForm.resetFields()
                  }}
                >
                  Back to login
                </Button>
              </Form.Item>
            </Form>
          )}
        </Space>
      </Card>
    </div>
  )
}
